import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  USER_ACTIVITY_ACTIONS,
  USER_ACTIVITY_GROUP_LABELS,
  USER_ACTIVITY_INFO,
  userActivityGroup,
  type AdminActivityLogSortBy,
  type AdminUserRow,
  type UserActivityAction,
  type UserActivityGroup,
  type UserActivityLogEntry,
} from '@ai-meeting/shared'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchAdminActivityLogs } from '@/services/admin'
import AdminDataTable, { type AdminColumn } from './AdminDataTable'
import AdminDateRange from './AdminDateRange'
import { dateInputToEndMs, dateInputToStartMs, formatDateTime } from './adminFormat'
import { useAdminTable } from './useAdminTable'

interface UserActivityDrawerProps {
  /** Whose log to show. The drawer is mounted only while one is open. */
  user: AdminUserRow
  onClose: () => void
}

interface ActivityLogFilters {
  userId: string
  action?: UserActivityAction
  dateFrom?: number
  dateTo?: number
}

/**
 * The function picker's options, grouped the way the summary tab groups them.
 *
 * A Map keeps the order of `USER_ACTIVITY_ACTIONS`, which is already written
 * grouped, so the picker reads in the same order as the report.
 */
const ACTION_GROUPS = [
  ...USER_ACTIVITY_ACTIONS.reduce((groups, action) => {
    const group = userActivityGroup(action)
    return groups.set(group, [...(groups.get(group) ?? []), action])
  }, new Map<UserActivityGroup, UserActivityAction[]>()),
]

/**
 * One person's activity log, over the Users tab (BRAI-677).
 *
 * A drawer rather than a centred modal because the table is six columns wide,
 * and rather than a screen of its own because the point is to glance at a user
 * without losing the page and search that found them.
 *
 * Portalled to `document.body` like every other dialog here. The admin screen
 * has no transform ancestor today, but `position: fixed` resolves against the
 * nearest transformed ancestor rather than the viewport, and a panel that
 * silently lands in the wrong place the day someone animates a wrapper is not
 * worth the one line this saves.
 *
 * Mounted only while open, so switching users starts from a clean page and an
 * unset function filter instead of the previous person's.
 */
export default function UserActivityDrawer({ user, onClose }: UserActivityDrawerProps) {
  const { t, locale } = useUiStrings()
  const [action, setAction] = useState<UserActivityAction | ''>('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const closeRef = useRef<HTMLButtonElement>(null)

  const filters = useMemo<ActivityLogFilters>(
    () => ({
      userId: user.id,
      action: action || undefined,
      dateFrom: dateInputToStartMs(dateFrom),
      dateTo: dateInputToEndMs(dateTo),
    }),
    [user.id, action, dateFrom, dateTo]
  )

  const table = useAdminTable<UserActivityLogEntry, AdminActivityLogSortBy, ActivityLogFilters>({
    fetcher: fetchAdminActivityLogs,
    defaultSortBy: 'createdAt',
    filters,
  })

  // Escape closes, and the close button takes focus on open so a keyboard
  // reader is inside the drawer rather than still on the row behind it.
  useEffect(() => {
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const columns: AdminColumn<UserActivityLogEntry, AdminActivityLogSortBy>[] = [
    {
      key: 'action',
      label: t.adminColFunction,
      sortBy: 'action',
      // Sorted on the stored name, where the summary tab sorts the same column
      // on the label. The difference is that this one pages: ordering by a
      // label the database has never seen would mean reading the whole table
      // to arrange ten rows. It still does the job asked of it — every press
      // of one function lands together — only the order between functions
      // follows the stored name, which the tooltip shows.
      render: (row) => (
        <span title={row.action}>{USER_ACTIVITY_INFO[row.action]?.label ?? row.action}</span>
      ),
    },
    {
      key: 'status',
      label: t.adminColStatus,
      sortBy: 'status',
      render: (row) =>
        row.status === 'failure' ? (
          <span className="text-red-600">{t.adminStatusFailure}</span>
        ) : (
          <span className="text-[var(--text-secondary)]">{t.adminStatusSuccess}</span>
        ),
    },
    {
      key: 'meeting',
      label: t.adminColMeeting,
      cellClassName: 'max-w-xs truncate',
      // A null title is a row whose meeting is gone, or one that never had a
      // meeting. Printing the raw uuid instead would tell a reader nothing.
      render: (row) => (
        <span title={row.meetingTitle ?? undefined}>{row.meetingTitle ?? '—'}</span>
      ),
    },
    {
      key: 'projectKey',
      label: t.adminColProject,
      render: (row) => row.projectKey ?? '—',
    },
    {
      key: 'createdAt',
      label: t.adminColWhen,
      sortBy: 'createdAt',
      render: (row) => formatDateTime(row.createdAt, locale),
    },
  ]

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-950/30 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-user-activity-title"
      data-testid="user-activity-drawer"
    >
      {/* Hidden from assistive tech and from the tab order: it would otherwise
          be a second button called "Close" in a dialog that already has one,
          and reading the backdrop out adds nothing a reader can act on. The
          header button and Escape are the real ways out; this is the mouse
          shortcut. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />

      <div className="relative flex h-full w-full max-w-3xl flex-col border-l border-[var(--border-color)] bg-[var(--bg-primary)] shadow-2xl">
        <header className="flex shrink-0 items-start gap-3 border-b border-[var(--border-color)] px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2
              id="admin-user-activity-title"
              className="truncate text-base font-bold text-[var(--text-primary)]"
            >
              {t.adminUserActivityTitle}
            </h2>
            <p className="mt-0.5 truncate text-xs text-[var(--text-secondary)]">
              {user.name} · {user.email}
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-[var(--border-color)] px-3 py-1.5 text-xs font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
          >
            {t.close}
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[12rem] flex-col gap-1 text-xs font-medium text-[var(--text-secondary)]">
              {t.adminFilterFunction}
              <select
                value={action}
                onChange={(event) => setAction(event.target.value as UserActivityAction | '')}
                className="h-10 rounded-lg border border-[var(--border-hover)] bg-[var(--bg-secondary)] px-2.5 text-sm text-[var(--text-primary)] shadow-[var(--shadow-sm)] transition-colors focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
              >
                <option value="">{t.adminFilterFunctionAll}</option>
                {ACTION_GROUPS.map(([group, actions]) => (
                  <optgroup key={group} label={USER_ACTIVITY_GROUP_LABELS[group]}>
                    {actions.map((option) => (
                      <option key={option} value={option}>
                        {USER_ACTIVITY_INFO[option].label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <AdminDateRange
              from={dateFrom}
              to={dateTo}
              onFromChange={setDateFrom}
              onToChange={setDateTo}
            />
          </div>

          <AdminDataTable
            columns={columns}
            rows={table.rows}
            rowKey={(row) => row.id}
            loading={table.loading}
            error={table.error}
            sortBy={table.sortBy}
            sortDir={table.sortDir}
            onToggleSort={table.toggleSort}
            page={table.page}
            totalPages={table.totalPages}
            total={table.total}
            onPageChange={table.setPage}
          />
        </div>
      </div>
    </div>,
    document.body
  )
}
